import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { Users, Timer, BookOpen, ListChecks, CalendarDays, BadgeCheck, MessageSquare, User } from 'lucide-react';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  ServiceHero, JumpNav, ContentSection, SplitSection, StepsSection,
  CardsSection, CompareSection, ChipsSection, CtaBlock, FaqSection, RelatedPages,
  type FaqItem, type RelatedPage,
} from "@/components/service-page/ServicePageSections";

const KEYWORDS = [
  "one on one tutoring", "one-on-one private tutoring", "online private tutor",
  "one to one tuition", "private tuition classes", "online one to one tutoring",
];

const FIND_TUTORS = { label: "Find Private Tutors", to: "/search/one-to-one" };

const IMG = {
  what:     { src: "/bookmycoaching-private-tutor-explaining-maths.webp", alt: "Private tutor explaining a maths problem to one student", width: 960, height: 720 },
  why:      { src: "/bookmycoaching-1on1-student-asking-a-doubt.webp", alt: "Student asking a doubt during a private lesson", width: 960, height: 1080 },
  how:      { src: "/bookmycoaching-1on1-online-and-offline-tuition-modes.webp", alt: "Online and offline private tuition modes", width: 720, height: 900 },
  find:     { src: "/bookmycoaching-1on1-search-filters.webp", alt: "Searching for a tutor with filters on BookMyCoaching", width: 900, height: 560 },
  school:   { src: "/bookmycoaching-1on1-school-subject-books.webp", alt: "School subject books", width: 600, height: 300 },
  exam:     { src: "/bookmycoaching-1on1-exam-preparation-materials.webp", alt: "Exam preparation materials", width: 600, height: 300 },
  skills:   { src: "/bookmycoaching-1on1-coding-and-language-learning.webp", alt: "Coding and language learning", width: 600, height: 300 },
  fees:     { src: "/bookmycoaching-1on1-parent-and-tutor-planning-schedule.webp", alt: "Parent and tutor planning a lesson schedule", width: 900, height: 400 },
  vsOne:    { src: "/bookmycoaching-1on1-one-on-one-lesson.webp", alt: "One-on-one lesson", width: 800, height: 450 },
  vsGroup:  { src: "/bookmycoaching-1on1-small-group-class.webp", alt: "Group class with a small batch", width: 800, height: 450 },
  ready:    { src: "/bookmycoaching-1on1-private-tutor-ready-to-teach.webp", alt: "Private tutor ready to teach", width: 600, height: 600 },
  faq:      { src: "/bookmycoaching-1on1-parent-and-student-question.webp", alt: "Parent and student with a question", width: 600, height: 450 },
};

const JUMP_ITEMS = [
  { id: "what",    label: "What is it?" },
  { id: "why",     label: "Why choose it?" },
  { id: "how",     label: "How it works" },
  { id: "find",    label: "How to find" },
  { id: "fees",    label: "Fees & scheduling" },
  { id: "vs",      label: "vs Group classes" },
  { id: "faq",     label: "FAQs" },
];

const RELATED: RelatedPage[] = [
  {
    label: "Group Classes",
    color: "#0E8A78",
    icon: Users,
    title: "Online Group Classes — Shared Batches & Set Timetables",
    excerpt: "Learn alongside peers on a fixed schedule. Compare batch fees and seat availability.",
    to: "/online-group-classes",
  },
  {
    label: "Crash Courses",
    color: "#D9472B",
    icon: Timer,
    title: "Exam Crash Courses — Fixed Sessions, Clear Timetable",
    excerpt: "Intensive programmes built around exam dates. One package price, defined session count.",
    to: "/exam-crash-courses",
  },
  {
    label: "Become a Tutor",
    color: "#262A7A",
    icon: BookOpen,
    title: "Become an Online Tutor in India with BookMyCoaching",
    excerpt: "Set your fees, choose your schedule and connect with students looking for your expertise.",
    to: "/become-an-online-tutor",
  },
];

const faqs: FaqItem[] = [
  {
    question: "What is the difference between 1-on-1 tutoring and group classes?",
    answer: "In 1-on-1 tutoring, one student learns with one teacher, and the lessons follow that student's pace. In group classes, several students learn together in a batch that follows a shared timetable.",
  },
  {
    question: "How much do private tuition classes cost in India?",
    answer: "There is no fixed price. Each teacher sets their own rate, either per session or as a package. The fee is shown on the listing, so you can compare different teachers before you decide.",
  },
  {
    question: "Can I find both online and offline private tutors?",
    answer: "Yes. Each listing shows whether the teacher offers online lessons, offline lessons or both. You can use the filters to search by mode and location.",
  },
  {
    question: "Can I choose weekly sessions or a fixed lesson package?",
    answer: "That depends on the teacher. Some offer recurring weekly lessons. Others offer a fixed number of sessions. The listing explains how the lessons are arranged and what the fee covers.",
  },
  {
    question: "How do I choose the right one-on-one tutor?",
    answer: "Start with the teacher's profile and published offering. Check the subject, teaching mode, fee and schedule. Look for the verification badge, then send an enquiry and ask about their teaching approach.",
  },
  {
    question: "Can I contact a tutor before choosing a class?",
    answer: "Yes. Create a free student account and send a private enquiry through the listing. Use that chance to ask about timings, fees and what the lessons will cover.",
  },
];

export default function PrivateSessionsPage() {
  const { pathname } = useLocation();
  const title = 'Find 1-on-1 Private Tutors for Personalised Learning | BookMyCoaching';
  const description = 'Looking for one on one tutoring that fits your subject, your level and your goals? Connect with verified teachers, check their schedules and choose the one who feels right for you.';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={getCanonicalUrl(pathname)} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={getCanonicalUrl(pathname)} />
        <meta property="og:image" content={getDefaultOgImage()} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <Header />

      <main>
        <ServiceHero
          badge="1-on-1 Tutoring"
          badgeColor="#2F6BD8"
          titleLead="Find 1-on-1 Private Tutors for"
          titleHighlight="Personalised Learning"
          intro="One teacher, one student. Lessons built around your subject, level and schedule. Connect with verified teachers and choose the one who feels right for you."
          primary={FIND_TUTORS}
          secondary={{ label: "How it works", to: "#how" }}
          facts={[
            { icon: ListChecks, label: "Individual attention" },
            { icon: CalendarDays, label: "Flexible schedule" },
            { icon: BadgeCheck, label: "Verified teachers" },
          ]}
          image="/bookmycoaching-one-to-one-small-size.gif"
        />

        <JumpNav items={JUMP_ITEMS} />

        {/* What */}
        <SplitSection
          id="what"
          title="What Are 1-on-1 Private Sessions?"
          image={IMG.what}
          paras={[
            "A 1-on-1 private session is simple. One teacher works with one student, and the whole lesson is built around that student.",
            "The teacher decides the subject, the teaching mode, the available timings and the fee. The fee can be set per session or as a package.",
            "Every listing on BookMyCoaching shows these details upfront, so you know what to expect before you reach out. There are no surprises, and you stay in control of your choice.",
          ]}
          bullets={[
            "One teacher, one student — the full session is yours",
            "Choose online, offline or both, depending on the listing",
            "Per-session rates or fixed-session packages",
            "Schedule that fits your routine, agreed with the teacher",
          ]}
          paras2={[
            "This format suits students who want clear explanations without feeling rushed. Parents also like it because they can see exactly what their child is learning and how often.",
          ]}
        />

        {/* Why */}
        <CardsSection
          id="why"
          title="Why Choose One-to-One Private Coaching?"
          imageSide="left"
          image={IMG.why}
          intro="In a private session, your questions and your pace come first. Nothing moves ahead until you are ready."
          shaded
          cards={[
            { icon: User,          title: "Individual attention",     description: "The full session is focused on you. A shy student who hesitates in a group often speaks up freely in a private lesson." },
            { icon: ListChecks,    title: "Lesson matches your level", description: "Content adapts to where you are right now, not an average class level. Weak topics get more time." },
            { icon: MessageSquare, title: "Quick, honest feedback",   description: "Mistakes get fixed early. You hear clearly what is working and what needs more practice." },
            { icon: CalendarDays,  title: "Flexible scheduling",      description: "Agree timings with the teacher. If your routine changes, you can discuss and rearrange." },
            { icon: BadgeCheck,    title: "Verified teachers",        description: "Teacher profiles are reviewed before approval. Look for the verification badge on each listing." },
            { icon: BookOpen,      title: "Any subject or exam",      description: "School subjects, JEE, NEET, UPSC, languages, coding — find teachers in any area you need." },
          ]}
          after={[
            "If you want clear explanations, quick feedback and lessons that match your exact needs, one-on-one private tutoring is a strong choice.",
          ]}
        />

        {/* How it works */}
        <StepsSection
          id="how"
          title="How Do Private Tutoring Sessions Work?"
          image={IMG.how}
          intro="Each teacher designs their own offering, so arrangements differ from one listing to the next. Read the listing fully before you send an enquiry."
          steps={[
            { title: "Check the listing",    description: "Review the subject, teaching mode (online or offline), session structure and fee." },
            { title: "Read the profile",     description: "Look at the teacher's background, qualifications and teaching approach." },
            { title: "Send an enquiry",      description: "Create a free student account and send a private message to the teacher." },
            { title: "Agree the details",    description: "Discuss timings, goals and expectations. Clear answers upfront keep everything on track." },
          ]}
          after={[
            "If you like learning from home, look for online one to one tutoring. Whether you pick an online private tutor or a teacher near you depends on your comfort and your routine. The mode is always shown on the listing.",
          ]}
        />

        {/* Who should choose — browse by subject */}
        <ChipsSection
          id="find"
          title="Browse 1-on-1 Tutors by Subject or Exam"
          image={IMG.find}
          intro="Search by subject, exam or learning goal. Then check which one-on-one private tutoring offerings are open right now."
          shaded
          groups={[
            {
              icon: BookOpen,
              title: "School subjects",
              image: IMG.school,
              chips: [
                { label: "Maths",   to: "/search?subject=maths&format=one-to-one" },
                { label: "Science", to: "/search?subject=science&format=one-to-one" },
                { label: "English", to: "/search?subject=english&format=one-to-one" },
                { label: "Hindi",   to: "/search?subject=hindi&format=one-to-one" },
                { label: "Social Studies", to: "/search?subject=social-studies&format=one-to-one" },
              ],
            },
            {
              icon: ListChecks,
              title: "Exam preparation",
              image: IMG.exam,
              chips: [
                { label: "JEE",   to: "/search?exam=JEE&format=one-to-one" },
                { label: "NEET",  to: "/search?exam=NEET&format=one-to-one" },
                { label: "UPSC",  to: "/search?exam=UPSC&format=one-to-one" },
                { label: "CAT",   to: "/search?exam=CAT&format=one-to-one" },
                { label: "GATE",  to: "/search?exam=GATE&format=one-to-one" },
              ],
            },
            {
              icon: MessageSquare,
              title: "Skills & languages",
              image: IMG.skills,
              chips: [
                { label: "Spoken English", to: "/search?subject=spoken-english&format=one-to-one" },
                { label: "Coding",         to: "/search?subject=coding&format=one-to-one" },
                { label: "French",         to: "/search?subject=french&format=one-to-one" },
                { label: "German",         to: "/search?subject=german&format=one-to-one" },
              ],
            },
          ]}
          after={[
            "Want flexibility? You can search for an online private tutor in any of these areas and compare profiles side by side. One-on-one private tutoring works best when you trust the teacher, so read each profile with care.",
          ]}
        />

        {/* Fees */}
        <ContentSection
          section={{
            id: "fees",
            title: "Private Tutoring Fees and Scheduling",
            image: IMG.fees,
            level: "h3",
            intro: [
              "There is no single fee or timetable for every private session. Teachers choose their own prices and availability, so each offering should be reviewed on its own.",
            ],
            bullets: [
              "Look for per-session rates or package prices",
              "Check whether the lessons are ongoing or fixed-length",
              "Discuss availability and expectations with the teacher",
            ],
            outro: [
              "Before you begin, talk to the teacher about timings, goals and what you want from the lessons. A short conversation up front keeps everyone on the same page.",
            ],
          }}
        />

        {/* Compare */}
        <CompareSection
          id="vs"
          title="1-on-1 vs Group Classes: Which Suits You?"
          level="h3"
          intro="Both formats give you teacher-led learning, but the experience is quite different."
          shaded
          cards={[
            {
              icon: User,
              title: "1-on-1 Private Sessions",
              description: "One teacher, one student. Lessons follow your pace and focus on your specific doubts and goals. Schedule agreed with the teacher.",
              current: true,
              image: IMG.vsOne,
            },
            {
              icon: Users,
              title: "Group Classes",
              description: "Several students in a shared batch with a fixed timetable. Peer interaction, often at a lower per-student fee.",
              link: { label: "Explore Group Classes", to: "/online-group-classes" },
              image: IMG.vsGroup,
            },
          ]}
          after={[
            "Not sure yet? Think about how you learn best. If you like to ask questions freely and move at your own speed, one to one tuition is a strong choice. If you enjoy studying with others and following a fixed timetable, a group class may feel better.",
          ]}
        />

        <CtaBlock
          title="Ready to Learn With Personal Guidance?"
          body="Explore the available options for one-on-one private tutoring and connect with a teacher who matches your goals. Prefer to learn from home? Try online one to one tutoring and send your first enquiry today."
          cta={FIND_TUTORS}
          image={IMG.ready}
        />

        <FaqSection faqs={faqs} cta={FIND_TUTORS} image={IMG.faq} />

        <RelatedPages pages={RELATED} />
      </main>
      <Footer />
    </div>
  );
}
